import React from 'react';
import { Box, Card, Chip, Container, Typography } from '@mui/material';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import StoreIcon from '@mui/icons-material/Store';

import visaLogo from '../../../../assets/visaLogo.png';
import masterLogo from '../../../../assets/masterLogo.png';
import ocaLogo from '../../../../assets/ocaLogo.png';
import amexLogo from '../../../../assets/amexLogo.png';
import { cuotasUruguay } from '../../../../constants';

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		root: {
			flexGrow: 1,
			paddingTop: theme.spacing(2),
			maxWidth: '100%',
		},
		cardsItem: {
			padding: '8px',
			display: 'flex',
			justifyContent: 'center',
			alignItems: 'center',
			'& img': {
				maxWidth: '100%',
				height: 'auto',
			},
		},
		cardBoxHolder: {
			marginTop: 12,
			display: 'grid',
			gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
			gap: theme.spacing(1),
			[theme.breakpoints.down('sm')]: {
				gridTemplateColumns: 'repeat(2, 1fr)',
				gap: theme.spacing(2),
			},
		},
		cardBox: {
			display: 'flex',
			flexDirection: 'column',
			alignItems: 'center',
			justifyContent: 'center',
			padding: theme.spacing(1),
			borderRadius: 8,
			border: '1px solid rgba(49, 25, 75, 0.08)',
			backgroundColor: 'rgba(49, 25, 75, 0.025)',
		},
		consulta: {
			marginTop: 14,
		},
		background: {
			backgroundColor: theme.palette.primary.main,
			borderRadius: '8px',
		},
		paymentMethodsGrid: {
			display: 'flex',
			flexWrap: 'wrap',
			gap: theme.spacing(1),
			marginTop: theme.spacing(1),
			marginBottom: theme.spacing(1),
		},
		paymentMethodItem: {
			display: 'inline-flex',
			alignItems: 'center',
		},
		paymentIcon: {
			marginRight: theme.spacing(0.5),
			color: theme.palette.secondary.main,
		},
		paymentText: {
			fontSize: '0.875rem',
		},
	}),
);

interface MevueloUYPaymentMethodsProps {
	isExternal?: boolean;
}

const MevueloUYPaymentMethods = ({ isExternal }: MevueloUYPaymentMethodsProps): JSX.Element => {
	const classes = useStyles();
	const accentColor = isExternal ? '#06036c' : '#31194B';
	const sectionTitleSx = {
		marginBottom: '10px',
		width: '100%',
		background: isExternal ? accentColor : `linear-gradient(135deg, ${accentColor} 0%, #4a246c 58%, #ED6602 155%)`,
		paddingLeft: '18px',
		paddingRight: '18px',
		paddingTop: '10px',
		paddingBottom: '10px',
		borderRadius: '10px',
		fontSize: '1rem',
		fontWeight: 700,
		color: 'white',
		boxShadow: '0 8px 20px rgba(49, 25, 75, 0.16)',
	};
	const paymentMethods = [
		{
			label: 'Transferencia bancaria',
			icon: <AccountBalanceWalletIcon className={classes.paymentIcon} fontSize="small" />,
		},
		{ label: 'Tarjetas de crédito', icon: <CreditCardIcon className={classes.paymentIcon} fontSize="small" /> },
		{ label: 'Pago en el local', icon: <StoreIcon className={classes.paymentIcon} fontSize="small" /> },
		{ label: 'Redes de cobranza', icon: <AccountBalanceIcon className={classes.paymentIcon} fontSize="small" /> },
	];
	const cardInstallments = [
		{ logo: visaLogo, alt: 'visa', installments: cuotasUruguay.visa },
		{ logo: amexLogo, alt: 'american-express', installments: cuotasUruguay.amex },
		{ logo: masterLogo, alt: 'master-card', installments: cuotasUruguay.master },
		{ logo: ocaLogo, alt: 'oca-card', installments: cuotasUruguay.oca },
	];

	return (
		<div className={classes.root}>
			<Container component="main" maxWidth="md">
				<Typography component="h1" variant="h6" sx={sectionTitleSx}>
					Nuestras formas de pago:
				</Typography>
				<Typography
					variant="body2"
					color="text.secondary"
					fontWeight={500}
					sx={{
						px: 1,
						pb: 1,
						borderLeft: '3px solid',
						borderColor: 'secondary.main',
						ml: 0.5,
						mb: 1,
					}}
				>
					Elegí la forma de pago que mejor se adapte a tu viaje. La cuotificación final puede depender del banco y la
					tarjeta.
				</Typography>
				<Card
					sx={{
						padding: '14px',
						marginBottom: '16px',
						borderRadius: '10px',
						border: '1px solid rgba(49, 25, 75, 0.10)',
						boxShadow: '0 6px 18px rgba(49, 25, 75, 0.08)',
					}}
				>
					<Typography variant="body2" fontWeight={800} color="primary">
						Medios disponibles
					</Typography>
					<Box className={classes.paymentMethodsGrid}>
						{paymentMethods.map((method) => (
							<Chip
								key={method.label}
								icon={method.icon}
								label={method.label}
								size="small"
								sx={{
									borderRadius: '999px',
									fontWeight: 700,
									bgcolor: 'rgba(49, 25, 75, 0.045)',
								}}
							/>
						))}
					</Box>
					<Typography variant="body2" fontWeight={800} color="primary" mt={1.5}>
						Cuotas con tarjetas
					</Typography>
					<Box className={classes.cardBoxHolder} display="flex" justifyContent="center" alignItems="baseline">
						{cardInstallments.map((card) => (
							<Box key={card.alt} display="flex" flexDirection="column" alignItems="center" className={classes.cardBox}>
								<Box className={classes.cardsItem}>
									<img src={card.logo} alt={card.alt} />
								</Box>
								<Typography variant="subtitle2" align="center" color="primary" fontWeight={800}>
									Hasta {card.installments} cuotas
								</Typography>
							</Box>
						))}
					</Box>
					<Typography
						className={classes.consulta}
						variant="body2"
						align="center"
						color="text.secondary"
						fontWeight={700}
					>
						Consultá por otras tarjetas y cuotificación{' '}
					</Typography>
				</Card>
			</Container>
		</div>
	);
};

export default MevueloUYPaymentMethods;
